<script lang="ts">
  import { onMount } from 'svelte'
  import { settingsActions } from '$lib/stores/settings'
  import { useError } from '$lib/hooks/useError'
  import { errorService } from '$lib/services/ErrorService'
  
  interface Shortcut {
    id: string
    name: string
    key: string
    keys: string[]
    editing: boolean
    global?: boolean
    isSystem?: boolean
    category?: string
  }
  
  // Default shortcuts
  const defaultShortcuts: Shortcut[] = [
    // General
    { id: 'toggle-window', name: '앱 표시/숨기기', key: 'Cmd+Shift+K', keys: ['Cmd', 'Shift', 'K'], editing: false, global: true, category: 'general' },
    { id: 'theme', name: '테마 변경', key: 'Cmd+Shift+T', keys: ['Cmd', 'Shift', 'T'], editing: false, category: 'general' },
    { id: 'settings', name: '설정', key: 'Cmd+,', keys: ['Cmd', ','], editing: false, category: 'general' },
    { id: 'fullscreen', name: '전체화면', key: 'F11', keys: ['F11'], editing: false, category: 'general' },
    
    // Navigation
    { id: 'focus-explorer', name: '파일 탐색기 포커스', key: 'Cmd+E', keys: ['Cmd', 'E'], editing: false, category: 'navigation' },
    { id: 'unfocus-editor', name: '에디터 포커스 해제', key: 'Escape', keys: ['Escape'], editing: false, category: 'navigation' },
    
    // Grammar & AI Analysis
    { id: 'check', name: '맞춤법 검사 & AI 분석', key: 'Cmd+Shift+G', keys: ['Cmd', 'Shift', 'G'], editing: false, category: 'grammar' },
    { id: 'apply-all', name: '모두 적용', key: 'Cmd+Shift+Enter', keys: ['Cmd', 'Shift', 'Enter'], editing: false, category: 'grammar' },
    
    // AI Features
    { id: 'generate', name: 'AI 프롬프트 열기', key: 'Cmd+K', keys: ['Cmd', 'K'], editing: false, category: 'ai' },
    { id: 'ai-paraphrase', name: 'AI 다시쓰기', key: 'Cmd+Shift+P', keys: ['Cmd', 'Shift', 'P'], editing: false, category: 'ai' },
    { id: 'ai-expand', name: 'AI 확장', key: 'Cmd+Shift+E', keys: ['Cmd', 'Shift', 'E'], editing: false, category: 'ai' },
    { id: 'ai-shrink', name: 'AI 축약', key: 'Cmd+Shift+D', keys: ['Cmd', 'Shift', 'D'], editing: false, category: 'ai' },
    { id: 'continue-sentence', name: '문장 이어쓰기', key: 'Cmd+Enter', keys: ['Cmd', 'Enter'], editing: false, category: 'ai' },
    { id: 'continue-paragraph', name: '단락 이어쓰기', key: 'Cmd+Enter (더블탭)', keys: ['Cmd', 'Enter', 'Enter'], editing: false, category: 'ai' },
    
    // Text Editing
    { id: 'toggle-link', name: '링크 편집', key: 'Cmd+L', keys: ['Cmd', 'L'], editing: false, category: 'editing' },
    { id: 'heading1', name: '제목 1', key: 'Option+Cmd+1', keys: ['Option', 'Cmd', '1'], editing: false, category: 'editing' },
    { id: 'heading2', name: '제목 2', key: 'Option+Cmd+2', keys: ['Option', 'Cmd', '2'], editing: false, category: 'editing' },
    { id: 'heading3', name: '제목 3', key: 'Option+Cmd+3', keys: ['Option', 'Cmd', '3'], editing: false, category: 'editing' },
    { id: 'strikethrough', name: '취소선', key: 'Option+Cmd+D', keys: ['Option', 'Cmd', 'D'], editing: false, category: 'editing' },
    { id: 'inline-code', name: '인라인 코드', key: 'Option+Cmd+C', keys: ['Option', 'Cmd', 'C'], editing: false, category: 'editing' },
    { id: 'clear', name: '텍스트 지우기', key: 'Cmd+Shift+L', keys: ['Cmd', 'Shift', 'L'], editing: false, category: 'editing' },
  ]
  
  // System shortcuts for conflict checking only (not displayed)
  const systemShortcuts: Shortcut[] = [
    { id: 'sys-copy', name: '복사', key: 'Cmd+C', keys: ['Cmd', 'C'], editing: false, isSystem: true },
    { id: 'sys-paste', name: '붙여넣기', key: 'Cmd+V', keys: ['Cmd', 'V'], editing: false, isSystem: true },
    { id: 'sys-cut', name: '잘라내기', key: 'Cmd+X', keys: ['Cmd', 'X'], editing: false, isSystem: true },
    { id: 'sys-undo', name: '실행 취소', key: 'Cmd+Z', keys: ['Cmd', 'Z'], editing: false, isSystem: true },
    { id: 'sys-redo', name: '다시 실행', key: 'Cmd+Shift+Z', keys: ['Cmd', 'Shift', 'Z'], editing: false, isSystem: true },
    { id: 'sys-selectall', name: '모두 선택', key: 'Cmd+A', keys: ['Cmd', 'A'], editing: false, isSystem: true },
    { id: 'sys-save', name: '저장', key: 'Cmd+S', keys: ['Cmd', 'S'], editing: false, isSystem: true },
    { id: 'sys-find', name: '찾기', key: 'Cmd+F', keys: ['Cmd', 'F'], editing: false, isSystem: true },
    { id: 'sys-find-files', name: '파일 검색', key: 'Cmd+Shift+F', keys: ['Cmd', 'Shift', 'F'], editing: false, isSystem: true },
    { id: 'sys-newtab', name: '새 탭', key: 'Cmd+T', keys: ['Cmd', 'T'], editing: false, isSystem: true },
    { id: 'sys-closetab', name: '탭 닫기', key: 'Cmd+W', keys: ['Cmd', 'W'], editing: false, isSystem: true },
    { id: 'sys-reload', name: '새로고침', key: 'Cmd+R', keys: ['Cmd', 'R'], editing: false, isSystem: true },
    { id: 'sys-bold', name: '굵게', key: 'Cmd+B', keys: ['Cmd', 'B'], editing: false, isSystem: true },
    { id: 'sys-italic', name: '기울임', key: 'Cmd+I', keys: ['Cmd', 'I'], editing: false, isSystem: true },
  ]
  
  // Error handling setup
  const { tryExecute, handleError } = useError('ShortcutsSettings')
  
  let shortcuts = $state<Shortcut[]>([])
  let recordingId = $state<string | null>(null)
  let recordedKeys = $state<string[]>([])
  let conflictingIds = $state<Set<string>>(new Set())
  
  // Load shortcuts from settings
  onMount(() => {
    const savedShortcuts = localStorage.getItem('korean-grammar:shortcuts')
    if (savedShortcuts) {
      try {
        const parsed = JSON.parse(savedShortcuts)
        shortcuts = defaultShortcuts.map(def => {
          const saved = parsed.find((s: any) => s.id === def.id)
          return saved ? { ...def, ...saved } : def
        })
      } catch (error) {
        handleError(error, {
          severity: 'warning',
          context: { action: 'loadShortcuts' },
          showToUser: false,
          logToConsole: true
        })
        errorService.showWarning('단축키 설정을 불러오는 중 오류가 발생했습니다. 기본 설정을 사용합니다.');
        shortcuts = [...defaultShortcuts]
      }
    } else {
      shortcuts = [...defaultShortcuts]
    }
    
    // Listen for keydown when recording
    const handleKeyDown = (e: KeyboardEvent) => {
      if (recordingId) {
        e.preventDefault()
        e.stopPropagation()
        
        // Check if trying to record ESC
        if (e.key === 'Escape') {
          // Special case: if recording for unfocus-editor, allow plain ESC
          if (recordingId === 'unfocus-editor' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
            recordedKeys = ['Escape']
            finishRecording()
          } else if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
            // ESC with modifiers, capture it as a shortcut
            captureKeys(e)
          } else {
            // Plain ESC cancels recording for other shortcuts
            cancelRecording()
          }
          return
        }
        
        captureKeys(e)
      }
    }
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (recordingId && recordedKeys.length > 0) {
        e.preventDefault()
        e.stopPropagation()
        finishRecording()
      }
    }
    
    window.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('keyup', handleKeyUp, true)
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('keyup', handleKeyUp, true)
    }
  })
  
  function startRecording(id: string) {
    // Don't allow editing system shortcuts
    const shortcut = shortcuts.find(s => s.id === id)
    if (shortcut?.isSystem) return
    
    // Don't allow recording if already conflicting
    if (conflictingIds.has(id)) return
    
    recordingId = id
    recordedKeys = []
  }
  
  function captureKeys(e: KeyboardEvent) {
    const keys: string[] = []
    
    // Capture modifiers
    if (e.metaKey || e.key === 'Meta') keys.push('Cmd')
    if (e.ctrlKey && e.key !== 'Control') keys.push('Ctrl')
    if (e.altKey && e.key !== 'Alt') keys.push('Option')
    if (e.shiftKey && e.key !== 'Shift') keys.push('Shift')
    
    // Capture main key
    if (!['Meta', 'Control', 'Alt', 'Shift'].includes(e.key)) {
      let key = e.key
      if (key === ' ') key = 'Space'
      else if (key === 'Escape') key = 'Escape'  // Handle Escape explicitly
      else if (key === 'ArrowUp') key = 'Up'
      else if (key === 'ArrowDown') key = 'Down'
      else if (key === 'ArrowLeft') key = 'Left'
      else if (key === 'ArrowRight') key = 'Right'
      else if (key.length === 1) key = key.toUpperCase()
      
      if (!keys.includes(key)) {
        keys.push(key)
      }
    }
    
    if (keys.length > 0) {
      recordedKeys = keys
    }
  }
  
  function finishRecording() {
    if (!recordingId || recordedKeys.length === 0) {
      cancelRecording()
      return
    }
    
    // Check for conflicts
    const conflict = checkConflict(recordingId, recordedKeys)
    if (conflict) {
      // Mark as conflicting but don't apply
      conflictingIds.add(recordingId)
      conflictingIds = new Set(conflictingIds) // Trigger reactivity
      // Keep the recorded keys visible but don't save
      shortcuts = shortcuts.map(s => 
        s.id === recordingId ? { ...s, keys: recordedKeys, key: recordedKeys.join('+') } : s
      )
    } else {
      applyShortcut(recordingId, recordedKeys)
    }
    
    recordingId = null
    recordedKeys = []
  }
  
  function checkConflict(id: string, keys: string[]): Shortcut | null {
    const keyStr = keys.join('+')
    
    // Check system shortcuts
    const systemConflict = systemShortcuts.find(s => 
      s.key === keyStr || arraysEqual(s.keys, keys)
    )
    if (systemConflict) return systemConflict
    
    // Check other app shortcuts
    const appConflict = shortcuts.find(s => 
      s.id !== id && (s.key === keyStr || arraysEqual(s.keys, keys))
    )
    if (appConflict) return appConflict
    
    return null
  }
  
  function arraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false
    const sortedA = [...a].sort()
    const sortedB = [...b].sort()
    return sortedA.every((val, index) => val === sortedB[index])
  }
  
  function applyShortcut(id: string, keys: string[]) {
    const keyStr = keys.join('+')
    shortcuts = shortcuts.map(s => 
      s.id === id ? { ...s, keys, key: keyStr } : s
    )
    saveShortcuts()
    
    // Remove from conflicting if it was there
    if (conflictingIds.has(id)) {
      conflictingIds.delete(id)
      conflictingIds = new Set(conflictingIds) // Trigger reactivity
    }
  }
  
  function cancelRecording() {
    recordingId = null
    recordedKeys = []
  }
  
  function clearConflict(id: string) {
    // Revert to previous saved value
    const savedShortcuts = localStorage.getItem('korean-grammar:shortcuts')
    if (savedShortcuts) {
      try {
        const parsed = JSON.parse(savedShortcuts)
        const saved = parsed.find((s: any) => s.id === id)
        if (saved) {
          shortcuts = shortcuts.map(s => 
            s.id === id ? { ...s, keys: saved.keys, key: saved.key } : s
          )
        } else {
          // Revert to default
          const defaultShortcut = defaultShortcuts.find(s => s.id === id)
          if (defaultShortcut) {
            shortcuts = shortcuts.map(s => 
              s.id === id ? { ...defaultShortcut } : s
            )
          }
        }
      } catch (error) {
        // Revert to default on error
        handleError(error, {
          severity: 'warning',
          context: { action: 'saveShortcut', shortcutId: id },
          showToUser: false
        })
        errorService.showWarning('단축키 저장에 실패했습니다. 기본값으로 복원됩니다.');
        const defaultShortcut = defaultShortcuts.find(s => s.id === id)
        if (defaultShortcut) {
          shortcuts = shortcuts.map(s => 
            s.id === id ? { ...defaultShortcut } : s
          )
        }
      }
    }
    
    conflictingIds.delete(id)
    conflictingIds = new Set(conflictingIds) // Trigger reactivity
  }
  
  function resetShortcut(id: string) {
    const defaultShortcut = defaultShortcuts.find(s => s.id === id)
    if (defaultShortcut) {
      shortcuts = shortcuts.map(s => 
        s.id === id ? { ...defaultShortcut } : s
      )
      saveShortcuts()
    }
  }
  
  function resetAllShortcuts() {
    if (confirm('모든 단축키를 기본값으로 초기화하시겠습니까?')) {
      shortcuts = [...defaultShortcuts]
      saveShortcuts()
    }
  }
  
  async function saveShortcuts() {
    const toSave = shortcuts.map(s => ({
      id: s.id,
      key: s.key,
      keys: s.keys
    }))
    localStorage.setItem('korean-grammar:shortcuts', JSON.stringify(toSave))
    
    // Update global shortcut if it's the toggle-window shortcut
    const toggleWindowShortcut = shortcuts.find(s => s.id === 'toggle-window')
    
    if (toggleWindowShortcut && window.api?.updateGlobalShortcut) {
      const [result, error] = await tryExecute(
        async () => {
          // Convert Proxy array to plain array for IPC
          const plainKeys = [...toggleWindowShortcut.keys]
          await window.api.updateGlobalShortcut({
            shortcutId: 'toggle-window',
            keys: plainKeys
          })
        },
        {
          severity: 'warning',
          context: { action: 'updateGlobalShortcut' },
          showToUser: false
        }
      )
      
      if (error) {
        errorService.showWarning('전역 단축키 업데이트에 실패했습니다.');
      }
    }
    
    // Dispatch event for other components to update
    window.dispatchEvent(new CustomEvent('shortcutsUpdated', { 
      detail: shortcuts 
    }))
  }
  
  function getDisplayKey(shortcut: Shortcut): string {
    if (recordingId === shortcut.id && recordedKeys.length > 0) {
      return recordedKeys.join('+')
    }
    return shortcut.key
  }
  
  function isRecording(id: string): boolean {
    return recordingId === id
  }
  
  function hasChanged(shortcut: Shortcut): boolean {
    const defaultShortcut = defaultShortcuts.find(s => s.id === shortcut.id)
    return defaultShortcut ? defaultShortcut.key !== shortcut.key : false
  }
</script>

<div class="shortcuts-settings">
  <h2 class="section-title">단축키 설정</h2>
  <p class="section-description">
    키보드 단축키를 설정합니다. 단축키를 클릭하여 변경할 수 있습니다.
  </p>
  
  <div class="shortcuts-list">
    {#each shortcuts as shortcut, i}
      {#if i === 0 || (shortcut.category && shortcuts[i-1].category !== shortcut.category)}
        <div class="category-section">
          <div class="category-header">
            <span class="category-title">
              {#if shortcut.category === 'general'}
                일반
              {:else if shortcut.category === 'navigation'}
                탐색
              {:else if shortcut.category === 'grammar'}
                문법 & 분석
              {:else if shortcut.category === 'ai'}
                AI 기능
              {:else if shortcut.category === 'editing'}
                텍스트 편집
              {/if}
            </span>
          </div>
        </div>
      {/if}
      <div class="shortcut-item {isRecording(shortcut.id) ? 'recording' : ''} {hasChanged(shortcut) ? 'changed' : ''} {conflictingIds.has(shortcut.id) ? 'has-conflict' : ''}">
        <div class="shortcut-info">
          <span class="shortcut-name">
            {shortcut.name}
            {#if shortcut.global}
              <span class="global-badge">전역</span>
            {/if}
          </span>
          {#if hasChanged(shortcut) && !conflictingIds.has(shortcut.id)}
            <button class="reset-btn" onclick={() => resetShortcut(shortcut.id)} title="기본값으로 초기화">
              ↺
            </button>
          {/if}
          {#if conflictingIds.has(shortcut.id)}
            <button class="reset-btn conflict-clear" onclick={() => clearConflict(shortcut.id)} title="충돌 해제">
              ✕
            </button>
          {/if}
        </div>
        
        <div class="shortcut-key-wrapper">
          {#if isRecording(shortcut.id)}
            <div class="recording-indicator">
              <span class="recording-dot"></span>
              <span class="recording-text">키 입력 대기중...</span>
            </div>
          {:else}
            <button 
              class="shortcut-key {shortcut.isSystem ? 'system' : 'editable'} {conflictingIds.has(shortcut.id) ? 'conflicting' : ''}"
              onclick={() => !shortcut.isSystem && !conflictingIds.has(shortcut.id) && startRecording(shortcut.id)}
              disabled={shortcut.isSystem || conflictingIds.has(shortcut.id)}
              title={shortcut.isSystem ? '시스템 단축키는 변경할 수 없습니다' : conflictingIds.has(shortcut.id) ? '다른 단축키와 충돌합니다' : '클릭하여 변경'}
            >
              {getDisplayKey(shortcut)}
            </button>
          {/if}
          {#if conflictingIds.has(shortcut.id)}
            <span class="conflict-hint">이미 사용중</span>
          {/if}
        </div>
      </div>
    {/each}
  </div>
  
  <div class="shortcuts-footer">
    <button class="reset-all-btn" onclick={resetAllShortcuts}>
      모든 단축키 초기화
    </button>
    <p class="shortcuts-hint">
      💡 단축키를 클릭하여 변경할 수 있습니다. ESC를 눌러 취소할 수 있습니다.
    </p>
  </div>
</div>

<style>
  .shortcuts-settings {
    padding: 24px;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px 0;
  }
  
  .section-description {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0 0 24px 0;
  }
  
  .shortcuts-list {
    padding: 0;
  }
  
  .shortcut-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-bottom: 1px solid var(--divider);
    transition: background-color 0.2s;
  }
  
  .shortcut-item:last-child {
    border-bottom: none;
  }
  
  .shortcut-item.recording {
    background: var(--bg-secondary);
  }
  
  .shortcut-item.changed {
    background: var(--bg-secondary);
  }
  
  .shortcut-item.has-conflict {
    background: rgba(239, 68, 68, 0.05);
  }
  
  .shortcut-info {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
  }
  
  .shortcut-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
  }
  
  .shortcut-key-wrapper {
    display: flex;
    align-items: center;
  }
  
  .shortcut-key {
    padding: 6px 12px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-primary);
    border-radius: 4px;
    font-size: 12px;
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace;
    color: var(--text-secondary);
    cursor: default;
    transition: all 0.2s;
    min-width: 100px;
    text-align: center;
  }
  
  .shortcut-key.editable {
    cursor: pointer;
  }
  
  .shortcut-key.editable:hover:not(:disabled) {
    background: var(--bg-secondary);
    border-color: var(--accent-primary);
    color: var(--text-primary);
  }
  
  
  .shortcut-key.conflicting {
    color: #ef4444;
    border-color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
    cursor: not-allowed;
  }
  
  .conflict-hint {
    margin-left: 8px;
    font-size: 11px;
    color: #ef4444;
    font-weight: 500;
  }
  
  .reset-btn.conflict-clear {
    background: rgba(239, 68, 68, 0.1);
    border-color: #ef4444;
    color: #ef4444;
  }
  
  .reset-btn.conflict-clear:hover {
    background: rgba(239, 68, 68, 0.2);
  }
  
  .global-badge {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: var(--accent-primary);
    color: white;
  }
  
  .reset-btn {
    width: 24px;
    height: 24px;
    padding: 0;
    background: transparent;
    border: 1px solid var(--border-primary);
    border-radius: 4px;
    color: var(--text-secondary);
    cursor: pointer;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }
  
  .reset-btn:hover {
    background: var(--bg-tertiary);
    color: var(--accent-primary);
    border-color: var(--accent-primary);
  }
  
  .recording-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    background: var(--bg-tertiary);
    border: 1px solid var(--accent-primary);
    border-radius: 4px;
    min-width: 100px;
  }
  
  .recording-dot {
    width: 8px;
    height: 8px;
    background: #ef4444;
    border-radius: 50%;
    animation: pulse 1.5s infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
  
  .recording-text {
    font-size: 12px;
    color: var(--text-secondary);
    font-weight: 500;
  }
  
  .shortcuts-footer {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .reset-all-btn {
    padding: 8px 16px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: 6px;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .reset-all-btn:hover {
    background: var(--bg-tertiary);
    border-color: var(--accent-primary);
  }
  
  .shortcuts-hint {
    font-size: 12px;
    color: var(--text-tertiary);
    margin: 0;
  }
  
  .category-section {
    margin-top: 20px;
    margin-bottom: 8px;
  }
  
  .category-section:first-child {
    margin-top: 0;
  }
  
  .category-header {
    display: flex;
    align-items: center;
    padding: 0 20px;
    margin-bottom: 8px;
    position: relative;
  }
  
  .category-title {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: var(--bg-primary);
    padding: 0 8px;
    position: relative;
    z-index: 1;
  }
  
  .category-header::before {
    content: '';
    position: absolute;
    left: 20px;
    right: 20px;
    top: 50%;
    height: 1px;
    background: var(--border-primary);
    opacity: 0.5;
    z-index: 0;
  }
  
  /* Dark mode handled by CSS variables */
</style>